// SEQ-180 phase 2, P24 / D26: the data layer behind the project P&L tab's
// editable operational rows.
//
// F18 was the uncomfortable fact this closes: a custom operational metric
// used to be a registry row with no value source and no entry surface, so
// every one of them read as 0 through the RPC's generic pivot. This hook
// reads the organisation's custom operational metrics FROM THE REGISTRY
// (never from app_settings.pnl_expense_categories, which must never contain
// a non-currency name: the V1 engine would sum it as a dollar deduction),
// and reads and writes their per-month values in pnl_manual_metric_value.
//
// It deliberately does NOT go through pnl_analytics_query. The RPC returns
// values coalesced and aggregated for reporting; this band needs the raw
// per-month rows, including the difference between "no row" and "a row
// holding 0", because that difference is what D27's Total rule turns on.
//
// 17 SEPTEMBER 2026, LABOUR HOURS. Hours stopped being payroll derived
// (20260917000100_pnl_manual_labour_hours.sql cut pnl_fact's two hours
// arms), so labour.ordinary_hours and labour.overtime_hours are typed here
// like any other operational metric. They are SYSTEM registry rows, so the
// query can no longer narrow to this organisation's own custom keys; see
// manualEntryRegistryFilter.ts for the rule and why it is a module.
// labour.overtime, the dollar figure, is still payroll derived and is not
// this hook's business.

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../hooks/useAuth";
import {
  admitsManualEntryRow,
  manualEntryRegistryFilter,
  manualMetricSortGroup,
} from "./manualEntryRegistryFilter";
import { resolveManualAggregation } from "./manualMetricTotals";
import type { ManualMetricAggregation } from "./manualMetricTotals";
import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";
import type { OperationalHoursMonthColumn } from "./useProjectOperationalHours";

/**
 * One operational metric the band lets a user type into: either one this
 * organisation defined, or a system key the database flags as manual input.
 *
 * `aggregation` is a `ManualMetricAggregation`, not the now-widened
 * `PnlAggregationMethod` (P7, commit 9508370, widened it to all six
 * methods). That is deliberate and stays: the band implements two folds,
 * and `resolveManualAggregation` is the one place the registry's wider
 * vocabulary narrows to them.
 */
export interface ManualOperationalMetric {
  metricKey: string;
  displayName: string;
  unit: PnlUnit;
  decimals: number;
  aggregation: ManualMetricAggregation;
  sortOrder: number;
  /**
   * True for a system row carrying is_manual_input (the two canonical labour
   * hours keys). It stays a system row: organization_id is still NULL, so it
   * is still undeletable and still not editable in the metric editor. The
   * band uses it only to order the rows and to word the tooltip.
   */
  isSystemManualInput: boolean;
}

export interface UseProjectManualMetricsResult {
  metrics: ManualOperationalMetric[];
  /** `metricKey` -> tab month key ('YYYY-MM') -> value. A missing entry is a month with no value, which is not the same as 0. */
  valuesByMetric: Map<string, Map<string, number>>;
  loading: boolean;
  /** Load failure message, or null. The rest of the P&L table keeps rendering either way. */
  error: string | null;
  /**
   * Writes one cell. `value === null` deletes the row, so clearing a cell
   * restores "no value" rather than storing a 0 that would skew an average.
   * Resolves to an error message, or null on success.
   */
  saveValue: (metricKey: string, columnKey: string, value: number | null) => Promise<string | null>;
}

const KNOWN_UNITS: readonly string[] = [
  "currency",
  "percent",
  "hours",
  "count",
  "ratio",
  "currency_per_hour",
];

/**
 * Narrows a registry row's `unit` text column to `PnlUnit`.
 *
 * `formatMetricValue` throws on an unknown unit, and a row created by a
 * future migration or a future picker option would otherwise take the
 * project P&L tab down with it. Falling back to 'count' renders a plain
 * number, which is wrong-looking at worst; dropping the row would hide a
 * metric the user created and typed values into.
 */
function narrowUnit(unit: string): PnlUnit {
  return (KNOWN_UNITS.includes(unit) ? unit : "count") as PnlUnit;
}

/** The tab's 'YYYY-MM' column key for a 'YYYY-MM-DD' period_month. */
function periodMonthToColumnKey(periodMonth: string): string {
  return periodMonth.slice(0, 7);
}

/** The first-of-month date pnl_manual_metric_value stores, for a 'YYYY-MM' column key. */
function columnKeyToPeriodMonth(columnKey: string): string {
  return `${columnKey}-01`;
}

interface RegistryRow {
  metric_key: string;
  display_name: string;
  unit: string;
  decimals: number | null;
  aggregation_method: string | null;
  sort_order: number | null;
  organization_id: string | null;
  is_manual_input: boolean | null;
}

interface ManualValueRow {
  metric_key: string;
  period_month: string;
  value: number | string;
}

export function useProjectManualMetrics(
  projectId: string,
  monthColumns: OperationalHoursMonthColumn[],
): UseProjectManualMetricsResult {
  const { organizationId } = useAuth();
  const [metrics, setMetrics] = useState<ManualOperationalMetric[]>([]);
  const [valuesByMetric, setValuesByMetric] = useState<Map<string, Map<string, number>>>(
    () => new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const firstColumnKey = monthColumns.length > 0 ? monthColumns[0].key : null;
  const lastColumnKey = monthColumns.length > 0 ? monthColumns[monthColumns.length - 1].key : null;

  useEffect(() => {
    let cancelled = false;

    async function load(orgId: string | null) {
      if (!orgId || !projectId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);

      const orFilter = manualEntryRegistryFilter(orgId);
      if (!orFilter) {
        setError("Your organisation could not be identified, so the operational metrics were not loaded.");
        setLoading(false);
        return;
      }

      /* eslint-disable @typescript-eslint/no-explicit-any -- pnl_metric_registry and pnl_manual_metric_value are not in the generated Supabase types (SEQ-180 phase 2; table lands with migration 20260910000400), and is_manual_input lands with 20260917000100 */
      // is_active is an AND filter and not part of orFilter on purpose: a
      // deactivated row must disappear whichever arm selected it. The
      // 17 September migration deactivates Ashleigh's two duplicate custom
      // rows, and without this the band would keep rendering them, empty,
      // next to the canonical keys that now hold her values.
      const registryQuery = (supabase as any)
        .from("pnl_metric_registry")
        .select(
          "metric_key, display_name, unit, decimals, aggregation_method, sort_order, organization_id, is_manual_input",
        )
        .eq("metric_role", "operational")
        .eq("kind", "input")
        .eq("is_active", true)
        .or(orFilter)
        .order("sort_order", { ascending: true });

      const { data: registryData, error: registryError } = (await registryQuery) as {
        data: RegistryRow[] | null;
        error: { message: string } | null;
      };

      if (cancelled) return;
      if (registryError) {
        setError(registryError.message);
        setLoading(false);
        return;
      }

      const resolved: ManualOperationalMetric[] = (registryData ?? [])
        // Re-checking what the `or` filter already asked for. PostgREST
        // takes that expression verbatim, so this is the assertion that a
        // mistake in it cannot put another organisation's metric on screen.
        .filter((row) => admitsManualEntryRow(row, orgId))
        .sort(
          (a, b) =>
            manualMetricSortGroup(a) - manualMetricSortGroup(b) ||
            (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
            a.display_name.localeCompare(b.display_name),
        )
        .map((row) => ({
          metricKey: row.metric_key,
          displayName: row.display_name,
          unit: narrowUnit(row.unit),
          decimals: row.decimals ?? 2,
          aggregation: resolveManualAggregation(row.unit, row.aggregation_method),
          sortOrder: row.sort_order ?? 0,
          isSystemManualInput: row.organization_id === null,
        }));

      setMetrics(resolved);

      if (resolved.length === 0 || !firstColumnKey || !lastColumnKey) {
        setValuesByMetric(new Map());
        setLoading(false);
        return;
      }

      const { data: valueData, error: valueError } = (await (supabase as any)
        .from("pnl_manual_metric_value")
        .select("metric_key, period_month, value")
        .eq("organization_id", orgId)
        .eq("project_id", projectId)
        .gte("period_month", columnKeyToPeriodMonth(firstColumnKey))
        .lte("period_month", columnKeyToPeriodMonth(lastColumnKey))) as {
        data: ManualValueRow[] | null;
        error: { message: string } | null;
      };
      /* eslint-enable @typescript-eslint/no-explicit-any */

      if (cancelled) return;
      if (valueError) {
        setError(valueError.message);
        setLoading(false);
        return;
      }

      const next = new Map<string, Map<string, number>>();
      for (const row of valueData ?? []) {
        // numeric comes back as a string over PostgREST when it exceeds
        // what JSON safely carries; Number() covers both shapes.
        const numeric = typeof row.value === "string" ? Number(row.value) : row.value;
        if (!Number.isFinite(numeric)) continue;
        const byMonth = next.get(row.metric_key) ?? new Map<string, number>();
        byMonth.set(periodMonthToColumnKey(row.period_month), numeric);
        next.set(row.metric_key, byMonth);
      }
      setValuesByMetric(next);
      setLoading(false);
    }

    load(organizationId);
    return () => {
      cancelled = true;
    };
  }, [organizationId, projectId, firstColumnKey, lastColumnKey]);

  const saveValue = useCallback(
    async (metricKey: string, columnKey: string, value: number | null): Promise<string | null> => {
      if (!organizationId) return "No organisation in context.";
      const periodMonth = columnKeyToPeriodMonth(columnKey);

      /* eslint-disable @typescript-eslint/no-explicit-any -- pnl_manual_metric_value is not in the generated Supabase types (SEQ-180 phase 2) */
      if (value === null) {
        const { data, error: deleteError } = (await (supabase as any)
          .from("pnl_manual_metric_value")
          .delete()
          .eq("organization_id", organizationId)
          .eq("project_id", projectId)
          .eq("period_month", periodMonth)
          .eq("metric_key", metricKey)
          .select("id")) as { data: unknown[] | null; error: { message: string } | null };

        if (deleteError) return deleteError.message;
        // A delete matching nothing is not a failure here: clearing a cell
        // that never had a value is a no-op, not a permission problem.
        void data;
      } else {
        const { data, error: upsertError } = (await (supabase as any)
          .from("pnl_manual_metric_value")
          .upsert(
            {
              organization_id: organizationId,
              project_id: projectId,
              period_month: periodMonth,
              metric_key: metricKey,
              value,
            },
            { onConflict: "organization_id,project_id,period_month,metric_key" },
          )
          .select("id")) as { data: unknown[] | null; error: { message: string } | null };

        if (upsertError) return upsertError.message;
        // Check the row count as well as the error. A write blocked by RLS
        // returns no error and no rows, which is how a "saved" value
        // silently reverts on the next read.
        if (!data || data.length === 0) {
          return "The value was not saved. You may not have permission to edit this project's P&L.";
        }
      }
      /* eslint-enable @typescript-eslint/no-explicit-any */

      setValuesByMetric((prev) => {
        const next = new Map(prev);
        const byMonth = new Map(next.get(metricKey) ?? []);
        if (value === null) byMonth.delete(columnKey);
        else byMonth.set(columnKey, value);
        next.set(metricKey, byMonth);
        return next;
      });
      return null;
    },
    [organizationId, projectId],
  );

  return useMemo(
    () => ({ metrics, valuesByMetric, loading, error, saveValue }),
    [metrics, valuesByMetric, loading, error, saveValue],
  );
}
